'use client'

import { useSearchPickerContext } from '@trustoo/components/searchPicker/SearchPickerContext'
import LinearLoader from '@trustoo/components/ui/LinearLoader'
import TopLayerPanel from '@trustoo/components/ui/TopLayerPanel'
import clsx from 'clsx'
import { ComponentPropsWithoutRef, KeyboardEvent, useCallback, useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'

interface SearchPickerPortalProps extends ComponentPropsWithoutRef<'div'> {
    showLoader?: boolean
}

/**
 * SearchPicker.Portal - Full-Screen Mobile Portal
 *
 * **Role in Compound Structure:**
 * This component renders the dropdown content in a full-screen portal for mobile devices.
 * It creates a portal to document.body and locks body scroll when active.
 *
 * **Responsibilities:**
 * - Renders dropdown content in a portal to document.body, or into the enclosing `<dialog>` when the
 *   picker sits in a modal: the modal inerts everything outside its subtree and paints over any
 *   z-index, so a body portal would be hidden behind it and unclickable. The overlay is a
 *   `TopLayerPanel`, which paints it above the modal and keeps it viewport-sized despite the
 *   dialog's `transform` and `overflow`.
 * - Locks body scroll when portal is open (mobile full-screen mode)
 * - Handles keyboard interactions (Escape to close)
 * - Conditionally renders only when picker is open
 * - Displays loading indicator when fetching options
 * - Provides full-screen overlay styling
 */
const SearchPickerPortal = ({
    children,
    className,
    showLoader = true,
    ...rest
}: SearchPickerPortalProps) => {
    const { isOpen, closePicker, isLoading, contentRef } = useSearchPickerContext()
    const dropdownRef = useRef<HTMLDivElement>(null)

    // Lock body scroll when the fullscreen dropdown is active
    useEffect(() => {
        if (typeof document === 'undefined') return

        if (isOpen) {
            const originalOverflow = document.body.style.overflow
            document.body.style.overflow = 'hidden'

            return () => {
                document.body.style.overflow = originalOverflow
            }
        }
    }, [isOpen])

    // Prevent click/touch events inside the portal from propagating past
    // document.body to window. Since the portal is rendered outside the
    // parent DOM tree, any ancestor with an outside-click listener on
    // window would incorrectly treat these events as "outside" clicks.
    // Using stopPropagation (not stopImmediatePropagation) ensures
    // React's own listener on body still fires, so synthetic onClick
    // handlers work normally.
    useEffect(() => {
        const el = dropdownRef.current
        if (!el) return

        const stopPropagation = (e: Event) => {
            if (el.contains(e.target as Node)) {
                e.stopPropagation()
            }
        }

        document.body.addEventListener('click', stopPropagation)
        document.body.addEventListener('touchend', stopPropagation)

        return () => {
            document.body.removeEventListener('click', stopPropagation)
            document.body.removeEventListener('touchend', stopPropagation)
        }
    }, [])

    const handleKeyDown = useCallback(
        (e: KeyboardEvent<HTMLDivElement>) => {
            if (e.key === 'Escape') {
                e.preventDefault()
                e.stopPropagation()
                closePicker()
            }
        },
        [closePicker],
    )

    if (!isOpen || typeof document === 'undefined') {
        return null
    }

    const content = (
        <TopLayerPanel
            ref={dropdownRef}
            onKeyDown={handleKeyDown}
            className={clsx(
                'fixed top-0 left-0 right-0 bottom-0 z-[9999] h-full w-full',
                'flex flex-col overflow-hidden bg-white',
                className,
            )}
            {...rest}>
            {showLoader && isLoading && <LinearLoader absolute className="z-[10000]" />}
            {children}
        </TopLayerPanel>
    )

    return createPortal(content, contentRef.current?.closest('dialog') ?? document.body)
}

export default SearchPickerPortal
